import "@infinite-table/infinite-react/index.css";
import {
	DataSource,
	type DataSourceDataParams,
	type DataSourceLivePaginationCursorFn,
	type DataSourcePropFilterValue,
	type DataSourcePropGroupBy,
	type DataSourceSingleSortInfo,
	InfiniteTable,
	type InfiniteTablePropColumns,
} from "@infinite-table/infinite-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { FilterParams, MetadataTypes, SortParams } from "epsm-api-types";
import {
	SCREENER_COLUMNS,
	type ScorerRequestColumn,
	type ScreenerColumn,
	type ScreenerData,
	type ScreenerState,
	type ScreenerSubField,
} from "epsm-api-types/models";
import { useCallback, useEffect, useState } from "react";
import type { OnStateChange } from "@/components/mosaic/ui/TileTitle/TileTitle";
import { Columns } from "@/components/ui/columns";
import { ColumnSet } from "@/components/ui/columnset";
import { Groups } from "@/components/ui/groups";
import { Toaster } from "@/components/ui/toaster";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { INFINITY_TABLE } from "@/config";
import { useFilterData } from "@/context/FilterDataContext";
import { useWidgetView } from "@/context/WidgetViewContext";
import { useCellContextMenuItems } from "@/hooks/useCellContextMenuItems";
import { useColumnMenuItems } from "@/hooks/useColumnMenuItems";
import { useColumnSizing } from "@/hooks/useColumnSizing";
import {
	columnPinning,
	MAX_COLUMN_WIDTH,
	MIN_COLUMN_WIDTH,
} from "@/hooks/useColumnSizing/configs";
import { useEventSubscription } from "@/hooks/useEventSubscription";
import { useHandleCellClick } from "@/hooks/useHandleCellClick";
import { getAlign, getParams } from "@/lib/utils";
import { isEnabledScorer } from "@/lib/utils/features";
import { getType } from "@/lib/utils/getType";
import { settings } from "@/lib/utils/settings/settings";
import ErrorHandler from "@/services/ErrorService/ErrorService";
import { DEFAULT_GRID_PAGE_SIZE } from "@/shared/constants/common";
import { mosaicSubToolbarClassNames } from "@/shared/constants/customStyleSets";
import { EVENTS } from "@/shared/eventEmiters/consts";
import type { EventEmitter } from "@/shared/eventEmiters/EventEmitter";
import { getSort } from "@/shared/helpers/getSort";
import { getSortInfo } from "@/shared/helpers/getSortInfo";
import type { ViewId } from "@/shared/types/common";
import { UseData as UsePriceData } from "../../Chart/services/UseData";
import { formatValue, parseNumber } from "../../helpers/FormattingHelpers";
import { exportCsvData } from "../../Monitor/lib/helpers/exportCsvData";
import { filterWidgetState } from "../../Monitor/lib/helpers/filterWidgetState";
import { renderSparklines } from "../../Monitor/lib/helpers/renderSparklines";
import { DEFAULT_ROWS_LIMIT, DEFAULT_STATE, scorerPrefix } from "../default";
import { useDataParamsChange } from "../hooks";
import { arraysEqualUnordered } from "../lib/helpers/arraysEqualUnordered/arraysEqualUnordered";
import { getFilterResult } from "../lib/helpers/getFilterResult";
import { processNestedSubfields } from "../lib/helpers/processNestedSubfields";
import { Filters } from "../ScreenerFilters/Filters/Filters";
import { Results } from "../ScreenerFilters/Results";
import { scorerStyle } from "../ScreenerScorer/const";
import { addNestedScorerSubfields } from "../ScreenerScorer/helpers/addNestedScorerSubfields/addNestedScorerSubfields";
import { scorerTotal } from "../ScreenerScorer/helpers/addScoringColumns/addScoringColumns";
import { flattenColumns } from "../ScreenerScorer/helpers/flattenResponseColumns";
import { Scorer } from "../ScreenerScorer/Scorer";
import { ScorerService } from "../services/ScorerService";
import { ScreenerColumnService } from "../services/ScreenerColumnService";
import { UseData } from "../services/UseData";
import type { WidgetScreenerState } from "../type";
import { ScreenerPresetDropdown } from "./components/ScreenerPresetDropdown";

const useFiltering = false;
const DEFAULT_PAGE_SIZE = DEFAULT_GRID_PAGE_SIZE;
const DEFAULT_FILTER_DELAY = 400;

const defaultFilterValue: DataSourcePropFilterValue<ScreenerData> | undefined =
	useFiltering ? [] : undefined;

const defaultGroupBy: DataSourcePropGroupBy<ScreenerData> = [];

let totalCount = 0;

export const Screener = ({
	state: widgetState,
	width,
	tileId,
	includeTotals = false,
	maxLimit,
	isSubToolbarShown = false,
	eventEmitter,
	onStateChange,
}: {
	state: WidgetScreenerState;
	width?: number;
	tileId: string;
	includeTotals?: boolean;
	maxLimit?: number;
	isSubToolbarShown?: boolean;
	eventEmitter: EventEmitter;
	onStateChange?: OnStateChange;
}) => {
	const { visibleColumns, setVisibleColumns } = useWidgetView();
	const { resultsRef, results, setResults } = useFilterData();

	const [isWindowExpanded, setIsWindowExpanded] = useState<boolean>(false);
	const [fieldsCounter, setFieldsCounter] = useState<Record<string, number>>(
		{},
	);

	const [state, setState] = useState<ScreenerState>({
		...DEFAULT_STATE,
		...filterWidgetState(DEFAULT_STATE, widgetState),
	});
	const [resultsScorer, setResultsScorer] = useState<ScorerRequestColumn[]>(
		widgetState?.scorerData ?? [],
	);
	const [priceData, setPriceData] = useState<
		Record<string, { field: number[] }>
	>({});

	const [shouldFetchScorer, setShouldFetchScorer] = useState<boolean>(false);

	const { execute, isLoading } = UseData.create().useGetData();
	const { execute: executePriceData, isLoading: isLoadingPriceData } =
		UsePriceData.create().useGetData();

	const isScorerEnabled = isEnabledScorer();

	useEffect(() => {
		eventEmitter.emit(EVENTS.ID, tileId);
	}, [eventEmitter, tileId]);

	useEventSubscription(eventEmitter, EVENTS.WIDGET_STATE, (data: any) => {
		const hasOnlyUserId =
			data &&
			typeof data === "object" &&
			Object.keys(data).length === 1 &&
			"userId" in data &&
			typeof data.userId === "string" &&
			!!data.userId.length;

		if (hasOnlyUserId) return;
		setDataParams((prevState) => ({
			...prevState,
			sortInfo: getSortInfo(data.sort),
		}));
		setState(data);
		resetColumnSize();
	});
	useEventSubscription(eventEmitter, EVENTS.SORT, (newSort: SortParams) => {
		setDataParams((prevState) => ({
			...prevState,
			sortInfo: getSortInfo(newSort),
		}));
		setState((prev) => ({ ...prev, sort: newSort }));
	});
	useEventSubscription(
		eventEmitter,
		EVENTS.EXPANDED_WINDOW,
		setIsWindowExpanded,
	);

	useEffect(() => {
		ScreenerColumnService.create()
			.getData()
			.then((columns) => {
				if (!state?.fields?.length) return;

				const columnMap: Record<string, ScreenerColumn> = {};

				for (const fieldPath of state.fields) {
					const mainField = fieldPath.startsWith(scorerPrefix)
						? fieldPath.split(".")[0].replace(scorerPrefix, "")
						: fieldPath.split(".")[0];

					const column = columns.data?.find((col) => col.name === mainField);
					if (column && fieldPath.startsWith(scorerPrefix)) {
						if (column.subFields?.length) {
							addNestedScorerSubfields(
								column,
								column.subFields,
								fieldPath.replace(scorerPrefix, ""),
								mainField,
								column.titleAbbreviation ||
									column.titleShort ||
									column.title ||
									column.name,
								columnMap,
							);
						} else {
							const { fncClassName: _fncClassName, ...valueWithoutFormatting } =
								column;
							columnMap[fieldPath] = {
								...valueWithoutFormatting,
								name: `${scorerPrefix}${column.name}`,
								title: `SC: ${column.title}`,
								...(column.titleAbbreviation && {
									titleAbbreviation: `SC: ${column.titleAbbreviation}`,
								}),
								...(column.titleShort && {
									titleShort: `SC: ${column.titleShort}`,
								}),
							};
						}
						continue;
					}
					if (fieldPath === scorerTotal.name) {
						columnMap[fieldPath] = scorerTotal;
						continue;
					}
					if (!column) continue;

					const filterSubFields = (
						subFields: ScreenerSubField[],
						currentPath: string[],
					): ScreenerSubField[] => {
						return subFields
							.filter((subField) => {
								const fullPath = [...currentPath, subField.name].join(".");

								return state.fields?.some((stateField) => {
									return stateField.startsWith(fullPath);
								});
							})
							.map((subField) => ({
								...subField,
								...(subField.subFields?.length && {
									subFields: filterSubFields(subField.subFields, [
										...currentPath,
										subField.name,
									]),
								}),
							}));
					};

					const updatedColumn = {
						...column,
						...(column.subFields?.length && {
							subFields: filterSubFields(column.subFields, [mainField]),
						}),
					};

					columnMap[mainField] = updatedColumn;
				}

				const filteredColumns = Object.values(columnMap);
				let resultColumns = filteredColumns;

				if (
					!isScorerEnabled &&
					filteredColumns.some((field) => field.name.startsWith(scorerPrefix))
				) {
					resultColumns = filteredColumns.filter(
						(field) => !field.name.startsWith(scorerPrefix),
					);
				}

				setVisibleColumns((prevVisibleColumns) => {
					return { ...prevVisibleColumns, Screener: resultColumns };
				});
			});
	}, [state.fields, setVisibleColumns, isScorerEnabled]);

	const {
		panelName,
		viewportReservedWidth,
		getColumnSizing,
		columnSizing,
		onColumnSizingChange,
		resetColumnSize,
	} = useColumnSizing(tileId);

	const [dataParams, setDataParams] = useState<
		Partial<DataSourceDataParams<ScreenerData>>
	>({
		groupBy: defaultGroupBy,
		sortInfo: getSortInfo(state?.sort),
		filterValue: defaultFilterValue,
		livePaginationCursor: 0,
	});

	const { data, fetchNextPage, isFetchingNextPage, refetch } = useInfiniteQuery(
		{
			queryKey: [
				"ScreenerData",
				state?.fields,
				dataParams.groupBy,
				dataParams.sortInfo,
				dataParams.filterValue,
				results,
				shouldFetchScorer,
			],
			queryFn: async ({ pageParam = 0 }) => {
				const params: DataSourceDataParams<ScreenerData> = {
					originalDataArray: [],
					livePaginationCursor: pageParam,
					groupBy: dataParams.groupBy,
					sortInfo: dataParams.sortInfo,
					filterValue: dataParams.filterValue,
				};

				return await getData(params);
			},
			initialPageParam: 0,
			getNextPageParam: (lastPage, allPages) => {
				if (lastPage?.hasMore) {
					return (allPages?.length ?? 0) * DEFAULT_PAGE_SIZE;
				}
				return undefined;
			},
			select: (data) => {
				const flatData = data.pages.flatMap((x) => {
					if (!x?.data.length) return [];

					return x.data;
				});

				const result = {
					pages: flatData,
				};

				return result;
			},
		},
	);

	useEventSubscription(eventEmitter, EVENTS.DOWNLOAD_CSV, async () => {
		let dataToDownload = data?.pages || [];
		if (data?.pages.length && data.pages.length < DEFAULT_ROWS_LIMIT) {
			const fullData = await getData(
				{
					originalDataArray: [],
					livePaginationCursor: 0,
					groupBy: dataParams.groupBy,
					sortInfo: dataParams.sortInfo,
					filterValue: dataParams.filterValue,
				},
				DEFAULT_ROWS_LIMIT,
			);
			dataToDownload = fullData?.data || [];
		}
		exportCsvData(visibleColumns.Screener, dataToDownload, "Screener")();
	});

	const fetchAndCacheTickerPrices = async () => {
		if (!data?.pages?.length) return priceData;

		const tickers = data.pages.map((page) => page?.ticker);
		const newTickers = tickers.filter((ticker) => !priceData[ticker]);

		if (!newTickers.length) return priceData;

		const fetchTickerPrices = async (ticker: string) => {
			const result = await executePriceData({
				params: {
					filter: [{ name: "ticker", condition: "eq", value: ticker }],
					fields: ["ticker", "adj_close"],
					sort: [{ name: "date", order: -1 }],
					pagination: { limit: 5, offset: 0 },
				},
			});

			return {
				ticker,
				field:
					result?.data
						?.map((price) => price.close)
						.toReversed()
						.filter((v) => v !== undefined) || [],
			};
		};

		const results = await Promise.allSettled(newTickers.map(fetchTickerPrices));

		const newPrices: Record<string, { field: number[] }> = { ...priceData };

		for (const result of results) {
			if (result.status === "fulfilled") {
				const { ticker, field } = result.value;
				newPrices[ticker] = { field };
			} else {
				ErrorHandler.logError(
					`Failed to fetch prices for ticker: ${result.reason}`,
				);
			}
		}

		setPriceData(newPrices);

		return newPrices;
	};

	const renderProcess = async (name: string) => {
		switch (name) {
			case "renderSparklines": {
				const prices = await fetchAndCacheTickerPrices();

				return {
					render: ({ data }: { data?: { ticker?: string } | null }) => {
						if (!data?.ticker || !prices) return;

						return renderSparklines(data.ticker, prices);
					},
				};
			}
		}
		return null;
	};

	// biome-ignore  lint/correctness/useExhaustiveDependencies: need panelMode as trigger
	const getColumns = useCallback(
		async (
			setColumnOrder: (newColumnOrder: string[]) => void,
		): Promise<InfiniteTablePropColumns<ScreenerData>> => {
			const newColumnOrder: string[] = [];
			const result: InfiniteTablePropColumns<ScreenerData> = {};

			for (const column of visibleColumns.Screener) {
				const columnName = column.name;

				if (column.subFields?.length) {
					processNestedSubfields(
						column,
						column.subFields,
						columnName,
						column.titleAbbreviation ||
							column.titleShort ||
							column.title ||
							column.name,
						newColumnOrder,
						result,
						getColumnSizing,
					);
				} else {
					newColumnOrder.push(columnName);

					result[columnName] = {
						field: columnName,
						...getColumnSizing(columnName),
						headerAlign: "start",
						align: getAlign(column.type),
						header: () => (
							<Tooltip>
								<TooltipTrigger asChild>
									<p className="text-ellipsis overflow-hidden whitespace-nowrap">
										{column.titleAbbreviation ||
											column.titleShort ||
											column.title}
									</p>
								</TooltipTrigger>
								<TooltipContent side="right" className="custom-fade-in">
									{column.title}
								</TooltipContent>
							</Tooltip>
						),
						type: getType(column.type),
						valueFormatter: ({ value }) =>
							column.options?.[value as keyof typeof column.options] ??
							formatValue(value, column.type, column.options),
						...(column.fncClassName && {
							className: ({ value }) =>
								column.fncClassName!(
									column.type === "num"
										? (parseNumber(value) as number)
										: value,
								),
						}),
						...(column.render && (await renderProcess(column.render))),
						...(columnName.startsWith(scorerPrefix) && { style: scorerStyle }),
					};
				}
			}

			// replace column order
			const screenerFields = state.fields || [];

			const justReplacedColumn = arraysEqualUnordered(
				screenerFields,
				newColumnOrder,
			);

			setColumnOrder(justReplacedColumn ? screenerFields : newColumnOrder);
			return result;
		},
		[visibleColumns.Screener, panelName],
	);

	useEffect(() => {
		if (!setFieldsCounter) return;

		const counter = results.reduce<Record<string, number>>((acc, result) => {
			const groupName = SCREENER_COLUMNS.find(
				(column) =>
					"name" in result.result[0] && column.name === result.result[0].name,
			)?.groupName;

			if (groupName) {
				acc[groupName] = (acc[groupName] || 0) + 1;
			}

			return acc;
		}, {});

		setFieldsCounter(counter);
	}, [results]);

	const getData = async (
		dataInfo: DataSourceDataParams<ScreenerData>,
		pageSize = maxLimit,
	) => {
		if (!state?.fields?.length) return;
		if (pageSize && dataInfo.livePaginationCursor) return;

		const types: MetadataTypes = {};

		for (const columnName of state.fields) {
			const [mainField] = columnName.split(".");
			const column = SCREENER_COLUMNS.find((item) => item.name === mainField);

			if (column) {
				types[columnName] = column.type;
			}
		}

		for (const filter of resultsRef.current.flatMap((r) => r.result)) {
			if ("name" in filter) {
				const [mainField] = filter.name.split(".");
				const column = SCREENER_COLUMNS.find((item) => item.name === mainField);

				if (column) {
					types[filter.name] = column.type;
				}
			}
		}

		const responseScreener =
			isScorerEnabled && widgetState.isScorerOpened && resultsScorer.length
				? undefined
				: await execute({
						params: {
							...getParams(
								dataInfo,
								state.fields.length ? state.fields : undefined,
								pageSize ?? DEFAULT_PAGE_SIZE,
							),
							filter: resultsRef.current.flatMap((r) => r.result),
							types,
						},
					});

		let resultData = responseScreener?.data || [];
		let hasNextPage = responseScreener?.metadata?.pagination?.nextPage;

		const sortParamsScorer = getParams(
			dataInfo,
			undefined,
			pageSize ?? DEFAULT_PAGE_SIZE,
		).sort?.map((item) => {
			const newName =
				item.name === scorerTotal.name
					? "score"
					: item.name.startsWith(scorerPrefix)
						? `columns.${item.name.replace(scorerPrefix, "")}.value`
						: `fields.${item.name}`;
			const newOrder =
				item.name === scorerTotal.name
					? item.order
					: item.name.startsWith(scorerPrefix)
						? -item.order
						: item.order;
			return {
				name: newName,
				order: newOrder as 1 | -1,
			};
		});

		const hiddenFieldsToSortBy =
			getParams(dataInfo, undefined, pageSize ?? DEFAULT_PAGE_SIZE)
				.sort?.map((item) => item.name)
				?.filter((item) => !state?.fields?.find((field) => field === item)) ??
			[];

		if (isScorerEnabled && widgetState.isScorerOpened && resultsScorer.length) {
			const responseScorer = await ScorerService.create().getData({
				columns: resultsScorer,
				pagination: getParams(
					dataInfo,
					undefined,
					pageSize ?? DEFAULT_PAGE_SIZE,
				).pagination,
				filter: resultsRef.current.flatMap((r) => r.result),
				fields: [...state.fields, ...hiddenFieldsToSortBy],
				sort: sortParamsScorer,
				types,
			});

			const mappedData = responseScorer?.data?.map((item) => {
				const mappedColumns = Object.fromEntries(flattenColumns(item.columns));
				return {
					...item.fields,
					[`${scorerPrefix}total`]: item.position,
					ticker: item.ticker,
					...mappedColumns,
				};
			});

			resultData = mappedData || [];
			hasNextPage = responseScorer?.metadata?.pagination?.nextPage;
		}

		if (responseScreener?.metadata?.total) {
			totalCount = responseScreener.metadata.total;
		}
		setShouldFetchScorer(false);
		return {
			data: resultData || [],
			hasMore: pageSize ? false : hasNextPage,
			totalCount,
		};
	};

	const [_scrollTopId, setScrollTop] = useState(0);

	useEffect(() => {
		// when sorting changes, scroll to the top
		setScrollTop(Date.now());
	}, []);

	useEffect(() => {
		const getDefaultResults = async () => {
			let cumulativeFilters: FilterParams = [];
			const cumulativeTypes: MetadataTypes = {};

			const filterResult = getFilterResult(state.filter);

			const promises = filterResult?.map(async (filterGroup) => {
				for (const filter of filterGroup) {
					if ("name" in filter) {
						const filterName = filter.name;
						const [mainField] = filterName.split(".");
						const column = SCREENER_COLUMNS.find(
							(item) => item.name === mainField,
						);

						if (column) {
							cumulativeTypes[filterName] = column.type;
						}
					}
				}

				cumulativeFilters = [...cumulativeFilters, ...filterGroup];

				const response = includeTotals
					? await execute({
							params: {
								filter: cumulativeFilters,
								total: "only",
								types: cumulativeTypes,
							},
						})
					: undefined;

				return {
					result: filterGroup,
					totalCount: response?.metadata?.total || 0,
				};
			});

			try {
				const defaultResults = promises ? await Promise.all(promises) : [];
				setResults(defaultResults);
				resultsRef.current = defaultResults;
			} catch (error) {
				ErrorHandler.logError(error);
			}
		};

		getDefaultResults();
	}, [state?.filter, execute, setResults, resultsRef, includeTotals]);

	const [columns, setColumns] = useState<
		InfiniteTablePropColumns<ScreenerData>
	>({});

	useEffect(() => {
		const columnsCall = async () => {
			const result = await getColumns((newColumnOrder: string[]) => {
				setState((prev) => ({
					...prev,
					fields: newColumnOrder,
				}));
			});
			setColumns(result);
		};
		columnsCall();
	}, [getColumns]);

	const livePaginationCursorFn: DataSourceLivePaginationCursorFn<ScreenerData> =
		useCallback(({ length }) => {
			return length;
		}, []);

	const onDataParamsChange = useDataParamsChange(setDataParams);

	const handleOnCellClick = useHandleCellClick({
		tileId,
		onTickerSelect: (ticker: string) => {
			onStateChange?.({ ticker });
		},
	});

	const getColumnMenuItems = useColumnMenuItems(
		"Screener" as ViewId,
		visibleColumns.Screener,
		setVisibleColumns,
	);

	const getCellContextMenuItems = useCellContextMenuItems();

	useEffect(() => {
		const filters = resultsRef.current.map((f) => f.result);
		const newState: ScreenerState = {
			...state,
			sort: getSort(dataParams.sortInfo),
			filter: filters.flat(),
		};

		eventEmitter.emit(EVENTS.WIDGET_PART_STATE, newState);
	}, [state, resultsRef.current, dataParams.sortInfo, eventEmitter]);

	const handleOnScrollToBottom = useCallback(
		() => !isFetchingNextPage && fetchNextPage(),
		[isFetchingNextPage, fetchNextPage],
	);

	return (
		<div
			id="full-h-el"
			className="flex flex-col h-full overflow-x-hidden"
			data-testid="screener-component"
		>
			{isSubToolbarShown && (
				<div className={mosaicSubToolbarClassNames}>
					<ScreenerPresetDropdown eventEmitter={eventEmitter} />
				</div>
			)}
			<div className="flex flex-col flex-1 overflow-x-auto">
				{isWindowExpanded && (
					<div
						className="flex gap-x-1 py-1 px-0.5 h-60"
						data-testid="expanded-window"
					>
						<Groups baseId="Screener" fieldsCounter={fieldsCounter} />
						<Columns
							baseId="Screener"
							width={width}
							results={results}
							isScorerOpened={widgetState.isScorerOpened}
						/>
						<ColumnSet
							baseId="Screener"
							isScorerOpened={widgetState.isScorerOpened}
						/>
						{isScorerEnabled && widgetState.isScorerOpened ? (
							<Scorer
								onStateChange={onStateChange}
								setResultsScorer={setResultsScorer}
								setShouldFetchScorer={setShouldFetchScorer}
								eventEmitter={eventEmitter}
								setScreenerState={setState}
							/>
						) : (
							<>
								<Filters width={width} refetch={refetch} />
								<Results refetch={refetch} />
							</>
						)}
					</div>
				)}
				<div className="h-full text-xs" data-testid="screener-table">
					<DataSource<ScreenerData>
						data={data?.pages ?? []}
						filterDelay={DEFAULT_FILTER_DELAY}
						primaryKey="ticker"
						sortInfo={dataParams.sortInfo}
						onSortInfoChange={(
							newSortInfo:
								| (DataSourceSingleSortInfo<ScreenerData> | null)
								| DataSourceSingleSortInfo<ScreenerData>[],
						) => {
							setState((prevState) => ({
								...prevState,
								sort: getSort(newSortInfo),
								filter: resultsRef.current.flatMap((r) => r.result),
							}));
						}}
						defaultFilterValue={defaultFilterValue}
						loading={isLoading || isLoadingPriceData || isFetchingNextPage}
						livePagination={true}
						livePaginationCursor={livePaginationCursorFn}
						onDataParamsChange={onDataParamsChange}
					>
						<InfiniteTable<ScreenerData>
							columns={columns}
							columnOrder={state?.fields || []}
							onColumnOrderChange={(newColumnOrder) => {
								setState((prev) => ({
									...prev,
									fields: newColumnOrder,
									filter: resultsRef.current.flatMap((r) => r.result),
								}));
							}}
							columnMaxWidth={MAX_COLUMN_WIDTH}
							columnMinWidth={MIN_COLUMN_WIDTH}
							columnPinning={
								data?.pages?.[0]?.ticker !== undefined ? columnPinning : {}
							}
							columnSizing={columnSizing}
							viewportReservedWidth={viewportReservedWidth}
							onColumnSizingChange={onColumnSizingChange}
							rowHeight={INFINITY_TABLE.ROW_HEIGHT}
							columnHeaderHeight={INFINITY_TABLE.COLUMN_HEADER_HEIGHT}
							licenseKey={settings.infiniteTableLicenseKey}
							columnDefaultFlex={1}
							onCellClick={handleOnCellClick}
							headerOptions={{
								alwaysReserveSpaceForSortIcon: false,
							}}
							getColumnMenuItems={getColumnMenuItems}
							getCellContextMenuItems={getCellContextMenuItems}
							onScrollToBottom={handleOnScrollToBottom}
						/>
					</DataSource>
				</div>
			</div>

			<Toaster scope={tileId} />
		</div>
	);
};
